/**
 * System Health Value Object
 * Represents the health status of the entire system and its services
 */

export type HealthStatus = "healthy" | "unhealthy" | "degraded";
export type ServiceStatus = "healthy" | "unhealthy";

export interface ServiceHealth {
    name: string;
    status: ServiceStatus;
    message?: string;
}

export interface DatabasePoolStatistics {
    size: number;
    checked_out: number;
    overflow: number;
    checked_in: number;
    total: number;
}

export interface DatabasePool {
    statistics: DatabasePoolStatistics;
    utilization_percent: number;
    status: ServiceStatus;
}

export interface HealthProps {
    status: HealthStatus;
    timestamp: Date;
    services: Record<string, ServiceHealth>;
    database_pool?: DatabasePool;
}

export class Health {
    private constructor(private readonly props: HealthProps) { }

    static create(props: HealthProps): Health {
        return new Health(props);
    }

    get status(): HealthStatus {
        return this.props.status;
    }

    get timestamp(): Date {
        return this.props.timestamp;
    }

    get services(): Record<string, ServiceHealth> {
        return this.props.services;
    }

    get databasePool(): DatabasePool | undefined {
        return this.props.database_pool;
    }

    /**
     * Get a specific service health status
     */
    getServiceHealth(serviceName: string): ServiceHealth | undefined {
        return this.props.services[serviceName];
    }

    /**
     * Check if all services are healthy
     */
    isAllHealthy(): boolean {
        return Object.values(this.props.services).every(
            (service) => service.status === "healthy"
        );
    }

    /**
     * Get count of healthy services
     */
    getHealthyCount(): number {
        return Object.values(this.props.services).filter(
            (service) => service.status === "healthy"
        ).length;
    }

    /**
     * Get count of unhealthy services
     */
    getUnhealthyCount(): number {
        return Object.values(this.props.services).filter(
            (service) => service.status === "unhealthy"
        ).length;
    }

    /**
     * Get total number of services
     */
    getTotalServices(): number {
        return Object.keys(this.props.services).length;
    }
}
